import { Design, UxOnly } from '@thive/fddm-types';

import {
  isHighwayArea,
  isManualArea,
  isSingularStationArea,
  isStationaryArea,
} from '@/modules/floorplanDesign/helpers/typeguards';
import { ElementLayerType, LayerNames, LayerType } from '@/modules/common/types/layers';
import { assertNever } from '@/helpers/utils';
import { FDDM_TAG, Tag } from '@/modules/floorplanDesign/constants';
import { Area } from '@/modules/floorplanDesign/types';

/**
 * @description Converts an area to its corresponding layer name.
 */
export const areaToLayerTag = (area: Area): Tag => {
  switch (area.areaType) {
    case 'HIGHWAY':
      return area.segments.length === 1 ? FDDM_TAG.LAYER_HIGHWAYS : FDDM_TAG.LAYER_ANGLED_HIGHWAYS;
    case 'RACKING': {
      switch (area.rackingLoadHandlingType) {
        case 'INTAKE':
          return FDDM_TAG.LAYER_INTAKE;
        case 'STORAGE':
          return FDDM_TAG.LAYER_STORAGE;
        case 'DELIVERY':
          return FDDM_TAG.LAYER_DELIVERY;
        default:
          assertNever(area.rackingLoadHandlingType);
      }
      break;
    }
    case 'SINGULAR': {
      const { loadHandlingType } = area;
      switch (loadHandlingType) {
        case 'INTAKE':
          return FDDM_TAG.LAYER_INTAKE;
        case 'STORAGE':
          return FDDM_TAG.LAYER_STORAGE;
        case 'DELIVERY':
          return FDDM_TAG.LAYER_DELIVERY;
        case 'OFF_VEHICLE_PROCESSING':
        case 'ON_VEHICLE_PROCESSING':
          return FDDM_TAG.LAYER_PROCESS;
        case 'INTAKE_AND_DELIVERY':
          return FDDM_TAG.LAYER_MANUAL;
        default:
          assertNever(loadHandlingType);
      }
      break;
    }
    case 'STATIONARY': {
      switch (area.stationaryType) {
        case 'PARKING':
          return FDDM_TAG.LAYER_PARKING;
        case 'CHARGING':
          return FDDM_TAG.LAYER_CHARGING;
        case 'INSERTION':
          return FDDM_TAG.LAYER_INSERTION;
        case 'EXTRACTION':
          return FDDM_TAG.LAYER_EXTRACTION;
        default:
          assertNever(area.stationaryType);
      }
      break;
    }
    case 'MANUAL': {
      return FDDM_TAG.LAYER_MANUAL;
    }
    default:
      assertNever(area);
  }
};

export const sortObjects = (design: Design, uxOnly: UxOnly): ElementLayerType[] => {
  const { singularStationAreas, stationaryAreas, highwayAreas, rackingAreas, manualAreas } =
    design.areas;
  const { obstacles, logicalSpaces } = design;
  const { groups } = uxOnly;

  const shapeStates: ElementLayerType[] = [
    ...singularStationAreas,
    ...stationaryAreas,
    ...highwayAreas,
    ...rackingAreas,
    ...manualAreas,
  ].map((area) => {
    if (isManualArea(area)) {
      return {
        id: area.id,
        type: 'MANUAL',
      };
    }
    if (isHighwayArea(area)) {
      return {
        id: area.id,
        type: area.segments.length > 1 ? 'ANGLED_HIGHWAY' : 'HIGHWAY',
      };
    }
    if (isSingularStationArea(area)) {
      return {
        id: area.id,
        type: area.loadHandlingType,
      };
    }
    if (isStationaryArea(area)) {
      return {
        id: area.id,
        type: area.stationaryType,
      };
    }
    return {
      id: area.id,
      type: area.rackingLoadHandlingType,
    };
  });

  shapeStates.push(
    ...obstacles.map((obstacle) => ({
      id: obstacle.id,
      type: obstacle.appearance,
    })),
  );

  shapeStates.push(
    ...logicalSpaces.map((logicalSpace) => ({
      id: logicalSpace.id,
      type: 'LOGICAL_SPACE' as LayerType,
    })),
  );

  shapeStates.push(
    ...groups.map((group) => ({
      id: group.id,
      type: 'GROUP' as LayerType,
    })),
  );

  return shapeStates;
};

export const getLayerOfLayerType = (type: LayerType): LayerNames | undefined => {
  switch (type) {
    case 'HIGHWAY':
      return LayerNames.HIGHWAYS;
    case 'ANGLED_HIGHWAY':
      return LayerNames.ANGLED_HIGHWAYS;
    case 'PILLAR':
    case 'BOX':
    case 'WARNING_ZONE':
      return LayerNames.OBSTACLES;
    case 'LOGICAL_SPACE':
      return LayerNames.LOGICAL_SPACES;
    case 'GROUP':
      return LayerNames.GROUPS;
    case 'WALL':
      return LayerNames.WALLS;
    case 'INTAKE':
      return LayerNames.INTAKE;
    case 'DELIVERY':
      return LayerNames.DELIVERY;
    case 'STORAGE':
      return LayerNames.STORAGE;
    case 'OFF_VEHICLE_PROCESSING':
    case 'ON_VEHICLE_PROCESSING':
      return LayerNames.PROCESS;
    case 'CHARGING':
      return LayerNames.CHARGING;
    case 'INSERTION':
      return LayerNames.INSERTION;
    case 'EXTRACTION':
      return LayerNames.EXTRACTION;
    case 'MANUAL':
    case 'INTAKE_AND_DELIVERY': // This type is only used for manual areas that have both intake and delivery load handling types, and should be placed on the manual layer
      return LayerNames.MANUAL;
    case 'PARKING':
      return LayerNames.PARKING;
    default:
      assertNever(type);
  }
};

export const getLayerGroupOfLayerType = (type: LayerType): LayerNames | undefined => {
  switch (type) {
    case 'HIGHWAY':
    case 'ANGLED_HIGHWAY':
      return LayerNames.ROADS;
    case 'PILLAR':
    case 'BOX':
    case 'WARNING_ZONE':
    case 'WALL':
      return LayerNames.INTERIOR;
    case 'INTAKE':
    case 'DELIVERY':
    case 'STORAGE':
    case 'OFF_VEHICLE_PROCESSING':
    case 'ON_VEHICLE_PROCESSING':
    case 'CHARGING':
    case 'INSERTION':
    case 'EXTRACTION':
    case 'PARKING':
    case 'MANUAL':
      return LayerNames.AREAS;
    case 'LOGICAL_SPACE':
      return LayerNames.LOGICAL_SPACES;
    case 'GROUP':
      return LayerNames.GROUPS;
    case 'INTAKE_AND_DELIVERY':
      return undefined; // Used for manual areas, which is handled differently
    default:
      assertNever(type);
  }
};

/**
 * Maps a layer type (shape kind) to its corresponding *name label* sub-layer
 * (AREA_NAMES / HIGHWAY_NAMES / OBSTACLE_NAMES).
 */
export const getNameLayerOfLayerType = (type: LayerType): LayerNames | undefined => {
  switch (type) {
    case 'INTAKE':
    case 'DELIVERY':
    case 'STORAGE':
    case 'OFF_VEHICLE_PROCESSING':
    case 'ON_VEHICLE_PROCESSING':
    case 'CHARGING':
    case 'INSERTION':
    case 'EXTRACTION':
    case 'PARKING':
    case 'MANUAL':
      return LayerNames.AREA_NAMES;
    case 'HIGHWAY':
    case 'ANGLED_HIGHWAY':
      return LayerNames.HIGHWAY_NAMES;
    case 'WALL':
    case 'PILLAR':
    case 'BOX':
    case 'WARNING_ZONE':
      return LayerNames.OBSTACLE_NAMES;
    case 'INTAKE_AND_DELIVERY':
    case 'LOGICAL_SPACE':
    case 'GROUP':
      return undefined;
    default:
      assertNever(type);
  }
};

export const getLayerTagsForFddmTag = (tag: Tag): readonly LayerNames[] => {
  switch (tag) {
    case FDDM_TAG.BUBBLE:
    case FDDM_TAG.BUBBLE_ARROW:
    case FDDM_TAG.BUBBLE_DIVIDER:
    case FDDM_TAG.LAYER_CONNECTION_BUBBLES:
      return [LayerNames.CONNECTIONS, LayerNames.CONNECTION_BUBBLES];
    case FDDM_TAG.CROSSING:
    case FDDM_TAG.INTER_AREA_CONNECTION:
    case FDDM_TAG.A_TO_B_CONNECTION:
    case FDDM_TAG.B_TO_A_CONNECTION:
    case FDDM_TAG.DISTANT_CONNECTION:
    case FDDM_TAG.LAYER_CONNECTIONS:
      return [LayerNames.CONNECTIONS];
    case FDDM_TAG.VEHICLE_LAYOUT_POI_CONNECTION:
      return [LayerNames.VEHICLE_LAYOUT_POI_CONNECTIONS];
    case FDDM_TAG.POINT_OF_INTEREST:
      return [LayerNames.GENERATED_POIS];
    case FDDM_TAG.STATION:
      return [LayerNames.GENERATED_STATIONS];
    case FDDM_TAG.HIGHWAY_AREA:
      return [LayerNames.AREAS, LayerNames.HIGHWAYS];
    case FDDM_TAG.MANUAL_AREA:
      return [LayerNames.AREAS, LayerNames.MANUAL];
    case FDDM_TAG.WALL:
      return [LayerNames.WALLS];
    case FDDM_TAG.OBSTACLE:
      return [LayerNames.OBSTACLES];
    case FDDM_TAG.LOGICAL_SPACE:
      return [LayerNames.LOGICAL_SPACES];
    case FDDM_TAG.GROUP:
      return [LayerNames.GROUPS];
    case FDDM_TAG.FLOW:
    case FDDM_TAG.LAYER_FLOWS:
      return [LayerNames.FLOWS];
    case FDDM_TAG.REFERENCE_IMAGE:
    case FDDM_TAG.REFERENCE_SCALING_LINE:
    case FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_A:
    case FDDM_TAG.REFERENCE_SCALING_POINT_CROSS_LINE_B:
      return [LayerNames.REFERENCE];
    case FDDM_TAG.ORIGIN:
      return [LayerNames.ORIGIN];
    default:
      return [];
  }
};
